import { Capacitor } from "@capacitor/core";
import { Purchases } from "@revenuecat/purchases-capacitor";

/**
 * Centralized wrapper around RevenueCat Capacitor SDK.
 * Handles initialization, identification, offerings, purchase and restore.
 */

const isNative = Capacitor.isNativePlatform();

// Replace with your actual Public SDK keys per platform
const IOS_PUBLIC_SDK_KEY =
  process.env.REACT_APP_RC_IOS_KEY || "appl_fUrcjmiVRHWTSElKaulKcJgdSNN"; // e.g., 'appl_xxx'

let configured = false;

const getPlatformKey = () => {
  const platform = Capacitor.getPlatform();
  if (platform === "ios") return IOS_PUBLIC_SDK_KEY;
  return "";
};

async function safeCall(fn, fallback = null) {
  try {
    return await fn();
  } catch (err) {
    console.warn("RevenueCatService error:", err?.message || err);
    return fallback;
  }
}

let lastAppUserID = null;

async function ensureConfigured() {
  if (!isNative) return false;
  if (configured) return true;

  const apiKey = getPlatformKey();
  if (!apiKey) {
    console.warn(
      "RevenueCat: Missing Public SDK Key. Set REACT_APP_RC_IOS_KEY."
    );
    return false;
  }

  const success = await safeCall(async () => {
    await Purchases.configure({
      apiKey,
      appUserID: lastAppUserID || undefined,
    });
    configured = true;
    return true;
  }, false);

  return success;
}

const RevenueCatService = {
  async configure(appUserID) {
    if (!isNative) return false;
    lastAppUserID = appUserID || lastAppUserID;
    return await ensureConfigured();
  },

  async logIn(appUserID) {
    if (!isNative || !appUserID) return null;
    return safeCall(async () => {
      const result = await Purchases.logIn({ appUserID });
      return result?.customerInfo || null;
    });
  },

  async logOut() {
    if (!isNative) return null;
    return safeCall(async () => {
      const result = await Purchases.logOut();
      return result?.customerInfo || null;
    });
  },

  async getOfferings() {
    if (!isNative) {
      return null;
    }

    const isConfigured = await ensureConfigured();
    if (!isConfigured) {
      return null;
    }

    try {
      const offerings = await Purchases.getOfferings();
      return offerings;
    } catch (err) {
      const msg = String(err?.message || err);

      if (msg.includes("must be configured")) {
        configured = false;
        const reconfigured = await ensureConfigured();
        if (reconfigured) {
          try {
            const offerings = await Purchases.getOfferings();
            return offerings;
          } catch (retryErr) {
            return null;
          }
        }
      }
      return null;
    }
  },

  async getCustomerInfo() {
    if (!isNative) return null;

    const isConfigured = await ensureConfigured();
    if (!isConfigured) {
      return null;
    }

    return safeCall(async () => {
      const customerInfo = await Purchases.getCustomerInfo();
      console.log("✅ RevenueCat initialized, current customer:", customerInfo);
      return customerInfo;
    });
  },

  isProFromCustomerInfo(customerInfo) {
    try {
      const entitlements =
        customerInfo?.customerInfo?.entitlements?.active ||
        customerInfo?.entitlements?.active ||
        {};

      // Check for the RevenueCat entitlement identifier (case-insensitive)
      return !!(
        entitlements["Vibe Check: Compatibility Quiz Pro"] ||
        entitlements["vibe check: compatibility quiz pro"] ||
        entitlements.pro ||
        entitlements.Pro ||
        entitlements.PRO
      );
    } catch (_) {
      return false;
    }
  },

  // Helper to check if there's a pending subscription change
  checkForPendingSubscriptionChange(customerInfo) {
    try {
      const info = customerInfo?.customerInfo || customerInfo;
      const activeSubscriptions = info?.activeSubscriptions || [];
      const subscriptionsByProductId =
        info?.subscriptionsByProductIdentifier || {};

      // Look for subscriptions that might have pending changes
      const pendingChanges = [];

      for (const productId of activeSubscriptions) {
        const subscription = subscriptionsByProductId[productId];
        if (subscription) {
          // Check if there's information about renewal product changes
          // This would typically be in the subscription renewal info
        }
      }

      return {
        hasPendingChanges: pendingChanges.length > 0,
        pendingChanges,
        note: "StoreKit may schedule subscription changes for next renewal date",
      };
    } catch (error) {
      return { hasPendingChanges: false, pendingChanges: [] };
    }
  },

  async purchasePackage(pkg, options = {}) {
    if (!isNative || !pkg) throw new Error("Package required");

    const isConfigured = await ensureConfigured();
    if (!isConfigured) {
      throw new Error("RevenueCat configuration failed - cannot purchase");
    }

    return safeCall(async () => {
      // For iOS, StoreKit handles subscription upgrades automatically when products
      // are in the same subscription group. We just need to purchase the new package.
      const result = await Purchases.purchasePackage({ aPackage: pkg });
      return result;
    });
  },

  async restorePurchases() {
    if (!isNative) return null;

    const isConfigured = await ensureConfigured();
    if (!isConfigured) {
      return null;
    }

    return safeCall(async () => {
      const result = await Purchases.restorePurchases();
      return result?.customerInfo || null;
    });
  },

  // DEBUG: Reset customer for testing (StoreKit only)
  async resetCustomerForTesting() {
    if (!isNative) return;

    try {
      // Log out current user
      await this.logOut();

      // Clear configuration
      configured = false;
      lastAppUserID = null;

      // Note: StoreKit transaction history persists and cannot be cleared programmatically
      // For a complete reset when switching RevenueCat projects:
      // 1. Uninstall the app completely from your device
      // 2. Reinstall and sign in again
      // OR use a different test device/simulator

      return true;
    } catch (error) {
      return false;
    }
  },
};

export default RevenueCatService;
